import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from 'src/api/apiClient';
import { RULE_DEFINITION_TEMPLATES_V1 } from 'src/constants/httpEndPoints';

export type OwnerOption = { label: string; value: string };

export type TemplateAggregate = {
  criteria?: Record<string, unknown>;
  sourceContactsFilters?: Record<string, unknown>;
  enrichment?: { enrichWithEmail: boolean; enrichWithPhone: boolean };
  assignContactsTo?: OwnerOption[];
  addToCampaign?: {
    selectedCMP: string;
    selectedSequence: string; // JSON stringified or id string depending on UI; normalize externally
    selectedMailBox?: string; // JSON stringified mailbox or id; normalize externally
  };
};

export type TemplateDetails = {
  id?: number | string;
  name?: string;
  description?: string;
  desc?: string;
  entityType?: 'CONTACT' | 'ACCOUNT' | string;
  aggregate?: TemplateAggregate;
  nodes?: Array<Record<string, unknown>>;
  edges?: Array<Record<string, unknown>>;
  templateType?: string;
  triggerType?: string;
  icon?: string;
  tags?: string[];
  metaData?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  isEdit?: boolean;
  selectedNodeId?: string | null;
};

export type TemplateSummary = {
  id: number | string;
  name: string;
  description?: string;
  entityType?: string;
  createdAt?: string;
  updatedAt?: string;
};

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type TemplatesState = {
  current: {
    data: TemplateDetails | null;
    status: AsyncStatus;
    error: string | null;
  };
  list: {
    items: TemplateSummary[];
    status: AsyncStatus;
    error: string | null;
  };
};

const initialState: TemplatesState = {
  current: { data: null, status: 'idle', error: null },
  list: { items: [], status: 'idle', error: null },
};

// Headers that many endpoints in this codebase expect
type CommonHeaders = {
  emailId: string;
  uniqueId: string;
};

// Thunks
export const fetchTemplates = createAsyncThunk<TemplateSummary[], { headers: CommonHeaders }>(
  'templates/fetchAll',
  async ({ headers }, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`${RULE_DEFINITION_TEMPLATES_V1}/`, { headers });
      const list = (res.data?.templates || res.data || []) as any[];
      // Map to summaries if needed
      return list.map((item: any) => ({
        id: item.id,
        name: item.name,
        description: item.desc ?? item.description,
        entityType: item.entityType,
        createdAt: item.created_at ?? item.createdAt,
        updatedAt: item.updated_at ?? item.updatedAt,
      }));
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to fetch templates');
    }
  },
);

export const fetchTemplateById = createAsyncThunk<
  TemplateDetails,
  { id: string | number; headers: CommonHeaders }
>('templates/fetchById', async ({ id, headers }, { rejectWithValue }) => {
  try {
    const res = await apiClient.get(`${RULE_DEFINITION_TEMPLATES_V1}/${id}`, { headers });
    const data = res.data || {};
    // Normalize into TemplateDetails; aggregate mapping is left to caller to compute if needed
    const details: TemplateDetails = {
      id: data.id ?? id,
      name: data.name ?? data.playbookName,
      description: data.description,
      desc: data.desc ?? data.description,
      entityType: data.entityType,
      nodes: (data.nodes as Array<Record<string, unknown>>) || [],
      edges: (data.edges as Array<Record<string, unknown>>) || [],
      templateType: data.templateType,
      triggerType: data.triggerType,
      oldTemplateType: data.oldTemplateType,
      icon: data.icon,
      tags: (data.tags as string[]) || [],
      metaData: data.metaData,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      aggregate: {
        criteria: (data.filters as Record<string, unknown>) || {},
      },
    };
    return details;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to fetch template');
  }
});

// Helper: build backend payload from our aggregate
const buildTemplatePayload = (input: {
  payload: {
    name?: string;
    templateType?: string;
    entityType?: string;
    desc?: string;
    icon?: string;
    tags?: string[];
    nodes?: Array<Record<string, unknown>>;
    edges?: Array<Record<string, unknown>>;
    isEdit?: boolean;
    metaData?: Record<string, unknown>;
  };
  headers: CommonHeaders;
}) => {
  const { payload, headers } = input;
  return {
    name: payload.name || 'New Template',
    tenant_id: headers.uniqueId,
    templateType: payload.templateType,
    entityType: payload.entityType,
    desc: payload.desc,
    icon: payload.icon,
    tags: payload.tags || [],
    metaData: payload.metaData || {},
    nodes: payload.nodes || [],
    edges: payload.edges || [],
  };
};

export const createTemplate = createAsyncThunk<
  TemplateDetails,
  { payload: any; headers: CommonHeaders }
>('templates/create', async ({ payload, headers }, { rejectWithValue }) => {
  try {
    const backendPayload = buildTemplatePayload({ payload, headers });
    const res = await apiClient.post(RULE_DEFINITION_TEMPLATES_V1, backendPayload, { headers });
    return res.data as TemplateDetails;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to create template');
  }
});

export const updateTemplate = createAsyncThunk<
  TemplateDetails,
  { id: string | number; payload: any; headers: CommonHeaders }
>('templates/update', async ({ id, payload, headers }, { rejectWithValue }) => {
  try {
    const backendPayload = buildTemplatePayload({ payload, headers });
    const res = await apiClient.put(`${RULE_DEFINITION_TEMPLATES_V1}/${id}`, backendPayload, {
      headers,
    });
    return res.data as TemplateDetails;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to update template');
  }
});

export const deleteTemplate = createAsyncThunk<
  { id: string | number },
  { id: string | number; headers: CommonHeaders }
>('templates/delete', async ({ id, headers }, { rejectWithValue }) => {
  try {
    await apiClient._delete(`${RULE_DEFINITION_TEMPLATES_V1}/${id}`, { headers });
    return { id };
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to delete template');
  }
});

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    clearCurrentTemplate(state) {
      state.current = { data: null, status: 'idle', error: null };
    },
    setCurrentTemplate(state, action: PayloadAction<TemplateDetails>) {
      state.current.data = action.payload;
      state.current.status = 'succeeded';
      state.current.error = null;
    },
    updateCurrentTemplate(state, action: PayloadAction<Partial<TemplateDetails>>) {
      state.current.data = { ...(state.current.data || {}), ...action.payload };
    },
    updateTemplateField(
      state,
      action: PayloadAction<{ key: keyof TemplateDetails | string; value: unknown }>,
    ) {
      if (!state.current.data) state.current.data = {} as TemplateDetails;
      (state.current.data as any)[action.payload.key] = action.payload.value as any;
    },
    setIsEdit(state, action: PayloadAction<boolean>) {
      if (!state.current.data) state.current.data = {} as TemplateDetails;
      state.current.data.isEdit = action.payload;
    },
    setSelectedNodeId(state, action: PayloadAction<string | null>) {
      if (!state.current.data) state.current.data = {} as TemplateDetails;
      state.current.data.selectedNodeId = action.payload ?? null;
    },
    setNodesEdges(
      state,
      action: PayloadAction<{
        nodes?: Array<Record<string, unknown>>;
        edges?: Array<Record<string, unknown>>;
      }>,
    ) {
      if (!state.current.data) state.current.data = {} as TemplateDetails;
      if (action.payload.nodes) state.current.data.nodes = action.payload.nodes;
      if (action.payload.edges) state.current.data.edges = action.payload.edges;
      // If selection is empty and nodes provided, select second or first
      if (
        action.payload.nodes &&
        action.payload.nodes.length > 0 &&
        state.current.data.selectedNodeId == null
      ) {
        const nodesArr = action.payload.nodes as Array<any>;
        state.current.data.selectedNodeId = nodesArr[0]?.id || null;
      }
    },
    updateNodeData(
      state,
      action: PayloadAction<{ nodeId: string; patch: Record<string, unknown> }>,
    ) {
      console.log('xyz--updateNodeData', action.payload);
      const nodes = (state.current.data?.nodes || []) as Array<any>;
      const idx = nodes.findIndex((n) => n.id === action.payload.nodeId);
      if (idx >= 0) {
        const prev = nodes[idx];
        const nextData = { ...(prev.data || {}), ...action.payload.patch };
        // Early return if nothing changed
        const keys = Object.keys(action.payload.patch);
        const noChange = keys.every((k) => (prev.data || {})[k] === nextData[k]);
        if (noChange) return;
        nodes[idx] = { ...prev, data: nextData };
        state.current.data = {
          ...(state.current.data || {}),
          nodes: [...nodes],
        } as TemplateDetails;
      }
    },
    upsertTemplateInList(state, action: PayloadAction<TemplateSummary>) {
      const idx = state.list.items.findIndex((t) => t.id === action.payload.id);
      if (idx >= 0) state.list.items[idx] = action.payload;
      else state.list.items.unshift(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.list.status = 'loading';
        state.list.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.list.status = 'succeeded';
        state.list.items = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.list.status = 'failed';
        state.list.error = (action.payload as string) || 'Failed to fetch templates';
      })
      .addCase(fetchTemplateById.pending, (state) => {
        state.current.status = 'loading';
        state.current.error = null;
      })
      .addCase(fetchTemplateById.fulfilled, (state, action) => {
        state.current.status = 'succeeded';
        const nodes = (action.payload.nodes || []) as Array<any>;
        const selectedNodeId = nodes[0]?.id || null;
        state.current.data = {
          ...action.payload,
          isEdit: true,
          selectedNodeId,
        };
      })
      .addCase(fetchTemplateById.rejected, (state, action) => {
        state.current.status = 'failed';
        state.current.error = (action.payload as string) || 'Failed to fetch template';
      })
      .addCase(createTemplate.pending, (state) => {
        state.current.status = 'loading';
        state.current.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.current.status = 'succeeded';
        const nodes = (action.payload.nodes || []) as Array<any>;
        const selectedNodeId = nodes[0]?.id || null;
        state.current.data = {
          ...action.payload,
          isEdit: true,
          selectedNodeId,
        };
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.current.status = 'failed';
        state.current.error = (action.payload as string) || 'Failed to create template';
      })
      .addCase(updateTemplate.pending, (state) => {
        state.current.status = 'loading';
        state.current.error = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.current.status = 'succeeded';
        const nodes = (action.payload.nodes || []) as Array<any>;
        const selectedNodeId = nodes[0]?.id || null;
        state.current.data = {
          ...action.payload,
          isEdit: true,
          selectedNodeId,
        };
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.current.status = 'failed';
        state.current.error = (action.payload as string) || 'Failed to update template';
      })
      .addCase(deleteTemplate.pending, (state) => {
        state.list.status = 'loading';
        state.list.error = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.list.status = 'succeeded';
        state.list.items = state.list.items.filter((t) => t.id !== action.payload.id);
        // If current is the one deleted, clear it
        if (state.current.data?.id === action.payload.id) {
          state.current = { data: null, status: 'idle', error: null };
        }
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.list.status = 'failed';
        state.list.error = (action.payload as string) || 'Failed to delete template';
      });
  },
});

export const {
  clearCurrentTemplate,
  setCurrentTemplate,
  updateCurrentTemplate,
  updateTemplateField,
  setIsEdit,
  setSelectedNodeId,
  setNodesEdges,
  updateNodeData,
  upsertTemplateInList,
} = templatesSlice.actions;

export default templatesSlice.reducer;

// Selectors
export const selectTemplatesList = (state: any) => state.templates.list.items as TemplateSummary[];
export const selectTemplatesListStatus = (state: any) => state.templates.list.status as AsyncStatus;
export const selectCurrentTemplate = (state: any) =>
  state.templates.current.data as TemplateDetails | null;
export const selectCurrentTemplateStatus = (state: any) =>
  state.templates.current.status as AsyncStatus;
export const selectCurrentAggregate = (state: any) =>
  state.templates.current.data?.aggregate as TemplateAggregate | undefined;
export const selectSelectedNodeId = (state: any) =>
  (state.templates.current.data?.selectedNodeId ?? null) as string | null;
